import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, MessageSquare, LayoutDashboard, LogOut, User, Menu, Wallet, Heart, ShieldAlert, Package, ArrowLeftRight, Home } from "lucide-react";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { seedData, updateMe } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import CurrencyToggle from "@/components/CurrencyToggle";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

// Buyer/Seller mode toggle stored in localStorage — Fiverr-style flip
const MODE_KEY = "noxi_mode";
function getStoredMode() {
  if (typeof window === "undefined") return "buyer";
  return localStorage.getItem(MODE_KEY) || "buyer";
}

export default function Layout() {
  const { user, logout, loading, setUser } = useAuth();
  const { code: currencyCode, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mode, setMode] = useState(getStoredMode());

  useEffect(() => {
    // Idempotent seed on first load — safe (no-op if data exists)
    seedData().catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When user's role changes, prefer that as default mode
  useEffect(() => {
    if (user?.role === "seller") { setMode("selling"); localStorage.setItem(MODE_KEY, "selling"); }
    else if (user?.role === "buyer") { setMode("buyer"); localStorage.setItem(MODE_KEY, "buyer"); }
  }, [user?.role]);

  // Force onboarding for signed-in users without a role (except when already there)
  useEffect(() => {
    if (!loading && user && !user.role && !user.is_admin && location.pathname !== "/onboarding") {
      navigate("/onboarding", { replace: true });
    }
  }, [loading, user, location.pathname, navigate]);

  const canSell = user?.role === "seller" || user?.role === "both";
  const inSellingMode = canSell && mode === "selling";

  const toggleMode = () => {
    const next = inSellingMode ? "buyer" : "selling";
    setMode(next);
    localStorage.setItem(MODE_KEY, next);
    if (next === "selling") navigate("/dashboard");
    else navigate("/buyer/home");
  };

  const becomeSeller = async () => {
    try {
      const fresh = await updateMe({ role: "both" });
      setUser(fresh);
      setMode("selling");
      localStorage.setItem(MODE_KEY, "selling");
      toast.success("Seller mode unlocked");
      navigate("/dashboard");
    } catch { toast.error("Failed to unlock seller mode"); }
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/browse?q=${encodeURIComponent(q.trim())}`);
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <AnnouncementBanner/>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 bg-background border-b border-black/10 transition-shadow ${scrolled ? "shadow-[0_1px_0_0_rgba(0,0,0,0.06)]" : ""}`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 flex items-center gap-4 lg:gap-6">
          <Link to="/" data-testid="logo-link" className="flex items-center group shrink-0">
            <img src="/noxi-logo.png" alt="Noxi" className="h-8 md:h-9 w-auto max-w-none shrink-0" />
          </Link>

          <form onSubmit={onSearch} className="hidden md:flex flex-1 min-w-[140px] lg:min-w-[220px] 2xl:min-w-[340px] max-w-2xl relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search branding, react, video editing..."
              className="pl-9 rounded-none border-foreground/20 focus-visible:ring-primary bg-card w-full"
            />
            <button
              type="submit"
              data-testid="header-search-btn"
              className="ml-2 px-4 shrink-0 bg-foreground text-background text-sm hover:bg-zinc-800 transition-colors mono"
            >
              SEARCH
            </button>
          </form>

          <nav className="flex items-center gap-3 lg:gap-4 ml-auto shrink-0">
            <Link to="/browse" data-testid="nav-explore" className="hidden lg:inline text-sm hover:text-primary transition-colors">
              Explore
            </Link>
            <Link to="/requests" data-testid="nav-requests" className="hidden lg:inline text-sm hover:text-primary transition-colors">
              Requests
            </Link>
            <CurrencyToggle/>

            {!loading && user ? (
              <>
                {canSell && (
                  <button
                    data-testid="mode-switch"
                    onClick={toggleMode}
                    className="hidden md:flex items-center gap-1.5 text-sm border border-foreground/20 px-3 py-1.5 hover:border-foreground transition-colors"
                    title={inSellingMode ? "Switch to Buying" : "Switch to Selling"}
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
                    <span className="mono text-xs hidden 2xl:inline">{inSellingMode ? "SWITCH TO BUYING" : "SWITCH TO SELLING"}</span>
                  </button>
                )}
                {!canSell && user.role && (
                  <button
                    data-testid="become-seller"
                    onClick={becomeSeller}
                    className="hidden md:inline text-sm hover:text-primary transition-colors"
                  >
                    Become a Seller
                  </button>
                )}
                <NotificationBell/>
                <Link to="/messages" data-testid="nav-messages" className="hidden md:flex items-center gap-1.5 text-sm hover:text-primary transition-colors">
                  <MessageSquare className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden lg:inline">Inbox</span>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger data-testid="user-menu-trigger" asChild>
                    <button className="flex items-center gap-2 group">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-foreground object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                          {user.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none border-foreground/20">
                    <DropdownMenuLabel className="mono text-xs opacity-70">{user.email}{user.is_admin && " · ADMIN"}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {/* Mobile-only nav items (duplicates of the header links that are hidden below md) */}
                    <DropdownMenuItem data-testid="mobile-menu-explore" onClick={() => navigate("/browse")} className="lg:hidden">
                      <Search className="w-4 h-4 mr-2" /> Explore
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="mobile-menu-requests" onClick={() => navigate("/requests")} className="lg:hidden">
                      <Package className="w-4 h-4 mr-2" /> Requests
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="mobile-menu-inbox" onClick={() => navigate("/messages")} className="md:hidden">
                      <MessageSquare className="w-4 h-4 mr-2" /> Inbox
                    </DropdownMenuItem>
                    {canSell && (
                      <DropdownMenuItem
                        data-testid="mobile-menu-mode-switch"
                        onClick={() => { toggleMode(); }}
                        className="md:hidden"
                      >
                        <ArrowLeftRight className="w-4 h-4 mr-2" />
                        {inSellingMode ? "Switch to Buying" : "Switch to Selling"}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuLabel className="md:hidden mono text-[10px] opacity-60 pt-2">◆ CURRENCY</DropdownMenuLabel>
                    <DropdownMenuItem
                      data-testid="mobile-menu-currency-usd"
                      onClick={() => setCurrency("USD")}
                      className="md:hidden"
                    >
                      <span className="w-4 inline-flex items-center justify-center mr-2">{currencyCode === "USD" ? "✓" : ""}</span>
                      <span className="mono text-xs mr-2 opacity-70">$</span> USD
                      <span className="ml-auto text-[10px] opacity-60">Global</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="mobile-menu-currency-inr"
                      onClick={() => setCurrency("INR")}
                      className="md:hidden"
                    >
                      <span className="w-4 inline-flex items-center justify-center mr-2">{currencyCode === "INR" ? "✓" : ""}</span>
                      <span className="mono text-xs mr-2 opacity-70">₹</span> INR
                      <span className="ml-auto text-[10px] opacity-60">India</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="md:hidden"/>
                    {!inSellingMode && (
                      <DropdownMenuItem data-testid="menu-buyer-home" onClick={() => navigate("/buyer/home")}>
                        <Home className="w-4 h-4 mr-2"/> Buyer home
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/seller/${user.user_id}`)}>
                      <User className="w-4 h-4 mr-2" /> Public profile
                    </DropdownMenuItem>
                    {canSell && (
                      <DropdownMenuItem data-testid="menu-seller-profile" onClick={() => navigate("/profile")}>
                        <User className="w-4 h-4 mr-2" /> Seller profile & payout
                        {!user.profile_complete && <span data-testid="profile-incomplete-dot" className="ml-auto w-2 h-2 rounded-full bg-destructive"/>}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> {inSellingMode ? "Dashboard" : "My orders"}
                    </DropdownMenuItem>
                    {canSell && (
                      <DropdownMenuItem data-testid="menu-earnings" onClick={() => navigate("/earnings")}>
                        <Wallet className="w-4 h-4 mr-2" /> Earnings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favorites")}>
                      <Heart className="w-4 h-4 mr-2" /> Saved gigs
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                          <ShieldAlert className="w-4 h-4 mr-2 text-primary" /> Admin panel
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {inSellingMode && (
                  <Link
                    to="/dashboard/new-gig"
                    data-testid="nav-post-gig"
                    className="hidden md:inline-block bg-primary text-primary-foreground px-4 py-2 mono text-xs font-medium hover:bg-primary/90 transition-colors"
                  >
                    + POST
                  </Link>
                )}
              </>
            ) : (
              <button
                onClick={login}
                data-testid="header-login-btn"
                className="bg-foreground text-background px-4 py-2 mono text-xs font-medium hover:bg-zinc-800 transition-colors"
              >
                SIGN IN
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-black/10 mt-24 py-12 bg-foreground text-background">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-8">
          <div>
            <img
              src="/noxi-logo-dark.png"
              alt="Noxi"
              className="h-9 md:h-10 w-auto mb-3"
              data-testid="footer-logo"
            />
            <p className="text-sm text-background/60 mt-2 max-w-xs">
              A brutalist marketplace where craft meets contract. Buyers get sharp work, sellers get paid.
            </p>
          </div>
          <div>
            <div className="overline text-background/70 mb-3">Marketplace</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/browse" className="hover:text-primary">Browse gigs</Link></li>
              <li><Link to="/requests" className="hover:text-primary">Buyer requests</Link></li>
              <li><Link to="/category/design" className="hover:text-primary">Design</Link></li>
              <li><Link to="/category/programming" className="hover:text-primary">Programming</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-background/70 mb-3">For sellers</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/dashboard" className="hover:text-primary">Dashboard</Link></li>
              <li><Link to="/dashboard/new-gig" className="hover:text-primary">Post a gig</Link></li>
              <li><Link to="/profile" className="hover:text-primary">Seller profile</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-background/70 mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link data-testid="footer-about" to="/about" className="hover:text-primary">About us</Link></li>
              <li><Link data-testid="footer-contact" to="/contact" className="hover:text-primary">Contact us</Link></li>
              <li><Link data-testid="footer-terms" to="/terms" className="hover:text-primary">Terms & Conditions</Link></li>
              <li><Link data-testid="footer-privacy" to="/privacy" className="hover:text-primary">Privacy Policy</Link></li>
              <li className="text-background/60 pt-2">© {new Date().getFullYear()} Noxi</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
